import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Printer, Plus, Trash2, ArrowLeft, Mail, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSite } from "@/lib/SiteContext";
import { RDHorizontal } from "@/components/brand/RDIdentity";
import { LogoMark } from "@/components/Logo";
import { useSeo } from "@/hooks/useSeo";

const inputCls = "focus-ring w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-500";
const labelCls = "mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500";

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);

const defaultState = {
  docType: "quote",
  docNo: "Q-2026-001",
  date: today(),
  validUntil: plusDays(14),
  custName: "",
  custSuburb: "",
  custPhone: "",
  custEmail: "",
  items: [{ desc: "Regular house cleaning — 3 bed / 2 bath", qty: 1, rate: "" }],
  notes: "Price includes all labour, equipment and standard cleaning products.",
  gst: true,
};

const money = (n) => (isNaN(n) || n === "" ? "—" : `$${Number(n).toFixed(2)}`);

export default function AdminDocuments() {
  useSeo("Documents | RD Cleaning Services", "Branded letterhead, quote and invoice templates.");
  const navigate = useNavigate();
  const { settings } = useSite();
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [doc, setDoc] = useState(() => {
    try {
      return { ...defaultState, ...JSON.parse(localStorage.getItem("rd_doc") || "{}") };
    } catch {
      return defaultState;
    }
  });

  useEffect(() => {
    api.get("/auth/me").catch(() => {
      localStorage.removeItem("rd_admin_token");
      navigate("/admin");
    });
  }, [navigate]);

  // Pre-fill from an enquiry ("Create Quote" button on the dashboard)
  useEffect(() => {
    try {
      const raw = localStorage.getItem("rd_doc_prefill");
      if (raw) {
        const pre = JSON.parse(raw);
        setDoc((d) => ({ ...d, ...pre, docType: "quote" }));
        localStorage.removeItem("rd_doc_prefill");
        toast.success("Enquiry details pre-filled — just add rates.");
      }
    } catch { /* ignore malformed prefill */ }
  }, []);

  useEffect(() => {
    localStorage.setItem("rd_doc", JSON.stringify(doc));
  }, [doc]);

  const set = (k) => (e) => setDoc((d) => ({ ...d, [k]: e.target.value }));
  const setItem = (i, k, v) =>
    setDoc((d) => ({ ...d, items: d.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) }));
  const addItem = () => setDoc((d) => ({ ...d, items: [...d.items, { desc: "", qty: 1, rate: "" }] }));
  const removeItem = (i) => setDoc((d) => ({ ...d, items: d.items.filter((_, j) => j !== i) }));

  const subtotal = doc.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
  const gstAmt = doc.gst ? subtotal * 0.1 : 0;
  const total = subtotal + gstAmt;
  const isQuote = doc.docType === "quote";
  const isLetter = doc.docType === "letterhead";
  const title = isQuote ? "QUOTE" : "INVOICE";

  const docPayload = () => ({
    to: doc.custEmail || "noreply@example.com",
    doc_type: doc.docType,
    doc_no: doc.docNo,
    date: doc.date,
    valid_until: doc.validUntil,
    cust_name: doc.custName,
    cust_suburb: doc.custSuburb,
    notes: doc.notes,
    gst: doc.gst,
    items: doc.items.map((i) => ({ desc: i.desc, qty: Number(i.qty) || 1, rate: Number(i.rate) || 0 })),
  });

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.post("/admin/quote-pdf", docPayload(), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RD-Cleaning-${isQuote ? "Quote" : "Invoice"}-${doc.docNo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded.");
    } catch {
      toast.error("Could not generate the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const emailDoc = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(doc.custEmail)) {
      toast.error("Enter the customer's email address first.");
      return;
    }
    if (!window.confirm(`Email this ${doc.docType} (with PDF attached) to ${doc.custEmail}?`)) return;
    setSending(true);
    try {
      await api.post("/admin/email-quote", docPayload());
      toast.success(`${isQuote ? "Quote" : "Invoice"} emailed to ${doc.custEmail} with the PDF attached`);
    } catch {
      toast.error("Could not send the email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="documents-page">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-navy no-print">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <LogoMark size={30} dark />
            <span className="font-display text-sm font-extrabold tracking-tight text-white">
              Documents <span className="text-teal-400">· Letterhead, Quotes & Invoices</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="focus-ring inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold text-white hover:border-teal-300 hover:text-teal-300" data-testid="docs-back-link">
              <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
            </Link>
            {!isLetter && (
              <button type="button" onClick={downloadPdf} disabled={downloading} className="focus-ring inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold text-white hover:border-teal-300 hover:text-teal-300 disabled:opacity-60" data-testid="docs-download-pdf-button">
                {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
                {downloading ? "Building…" : "Download PDF"}
              </button>
            )}
            {!isLetter && (
              <button type="button" onClick={emailDoc} disabled={sending} className="focus-ring inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold text-white hover:bg-white/20 disabled:opacity-60" data-testid="docs-email-button">
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                {sending ? "Sending…" : "Email PDF to Customer"}
              </button>
            )}
            <button type="button" onClick={() => window.print()} className="focus-ring inline-flex items-center gap-1.5 rounded-full bg-teal-500 px-4 py-1.5 text-xs font-bold text-navy-deep hover:bg-teal-400" data-testid="docs-print-button">
              <Printer className="h-3.5 w-3.5" /> Print / Save as PDF
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[380px_1fr]">
        {/* Editor — screen only */}
        <div className="no-print h-fit space-y-5 rounded-2xl border border-slate-200 bg-white p-6 lg:sticky lg:top-24">
          <div>
            <span className={labelCls}>Document Type</span>
            <div className="grid grid-cols-3 gap-2" role="tablist" aria-label="Document type">
              {["quote", "invoice", "letterhead"].map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={doc.docType === t}
                  onClick={() => setDoc((d) => ({ ...d, docType: t }))}
                  className={`focus-ring rounded-full px-3 py-2 text-xs font-bold capitalize transition-colors ${doc.docType === t ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:text-navy"}`}
                  data-testid={`doc-type-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          {!isLetter && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="doc-no" className={labelCls}>{isQuote ? "Quote" : "Invoice"} №</label>
                  <input id="doc-no" className={inputCls} value={doc.docNo} onChange={set("docNo")} data-testid="doc-no-input" />
                </div>
                <div>
                  <label htmlFor="doc-date" className={labelCls}>Date</label>
                  <input id="doc-date" type="date" className={inputCls} value={doc.date} onChange={set("date")} data-testid="doc-date-input" />
                </div>
              </div>
              <div>
                <label htmlFor="doc-valid" className={labelCls}>{isQuote ? "Valid Until" : "Due Date"}</label>
                <input id="doc-valid" type="date" className={inputCls} value={doc.validUntil} onChange={set("validUntil")} data-testid="doc-valid-input" />
              </div>
              <div>
                <label htmlFor="cust-name" className={labelCls}>Customer Name</label>
                <input id="cust-name" className={inputCls} value={doc.custName} onChange={set("custName")} placeholder="Jane Citizen" data-testid="cust-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="cust-suburb" className={labelCls}>Suburb</label>
                  <input id="cust-suburb" className={inputCls} value={doc.custSuburb} onChange={set("custSuburb")} placeholder="Scarborough" data-testid="cust-suburb-input" />
                </div>
                <div>
                  <label htmlFor="cust-phone" className={labelCls}>Phone</label>
                  <input id="cust-phone" className={inputCls} value={doc.custPhone} onChange={set("custPhone")} data-testid="cust-phone-input" />
                </div>
              </div>
              <div>
                <label htmlFor="cust-email" className={labelCls}>Customer Email</label>
                <input id="cust-email" type="email" className={inputCls} value={doc.custEmail} onChange={set("custEmail")} placeholder="customer@example.com" data-testid="cust-email-input" />
              </div>
              <div>
                <label className={labelCls}>Line Items</label>
                <div className="space-y-2" data-testid="doc-items">
                  {doc.items.map((it, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input className={inputCls} placeholder="Service description" value={it.desc} onChange={(e) => setItem(i, "desc", e.target.value)} data-testid={`doc-item-desc-${i}`} />
                      <input className={inputCls + " !w-16"} type="number" min="1" aria-label="Quantity" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`doc-item-qty-${i}`} />
                      <input className={inputCls + " !w-24"} type="number" min="0" step="0.01" aria-label="Rate" placeholder="Rate" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} data-testid={`doc-item-rate-${i}`} />
                      <button type="button" onClick={() => removeItem(i)} className="focus-ring rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label="Remove item" data-testid={`doc-item-remove-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addItem} className="focus-ring mt-2 inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-navy hover:border-teal-500 hover:text-teal-700" data-testid="doc-item-add">
                  <Plus className="h-3.5 w-3.5" /> Add Item
                </button>
              </div>
              <div>
                <label htmlFor="doc-notes" className={labelCls}>Notes</label>
                <textarea id="doc-notes" rows={3} className={inputCls} value={doc.notes} onChange={set("notes")} data-testid="doc-notes-input" />
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" checked={doc.gst} onChange={(e) => setDoc((d) => ({ ...d, gst: e.target.checked }))} className="h-4 w-4 accent-teal-600" data-testid="doc-gst-toggle" />
                Show GST (10%)
              </label>
            </>
          )}
          {isLetter && (
            <p className="text-sm text-slate-500">The letterhead is ready to print — type your letter directly onto the printed sheet, or copy your text into your word processor using this header.</p>
          )}
          <p className="rounded-xl bg-slate-50 p-3 text-xs leading-relaxed text-slate-500">
            Tip: use your browser's "Save as PDF" option in the print dialog to keep a copy, or use "Email to Customer" to send it straight from here.
          </p>
        </div>

        {/* A4 Preview / print area */}
        <div className="print-doc mx-auto w-full max-w-[794px] bg-white shadow-xl" data-testid="doc-preview">
          {/* Branded header */}
          <div className="flex items-start justify-between border-b-4 border-navy px-12 pb-6 pt-10">
            <RDHorizontal size={52} />
            {!isLetter && (
              <div className="text-right">
                <p className="font-display text-3xl font-extrabold tracking-tight text-navy">{title}</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-teal-700">{doc.docNo}</p>
              </div>
            )}
          </div>

          {!isLetter ? (
            <div className="px-12 py-8">
              {/* Meta + parties */}
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-teal-700">From</p>
                  <p className="mt-2 font-display text-sm font-bold text-navy">RD Cleaning Services</p>
                  <p className="text-xs leading-relaxed text-slate-600">
                    {settings.service_area}<br />
                    {settings.phone}<br />
                    {settings.email}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-teal-700">Prepared For</p>
                  <p className="mt-2 font-display text-sm font-bold text-navy">{doc.custName || "[Customer Name]"}</p>
                  <p className="text-xs leading-relaxed text-slate-600">
                    {doc.custSuburb || "[Suburb]"}<br />
                    {doc.custPhone || "[Phone]"} {doc.custEmail && <>· {doc.custEmail}</>}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex gap-8 border-y border-slate-200 py-3 font-mono text-[11px] uppercase tracking-wider text-slate-500">
                <span>Date: <strong className="text-navy">{doc.date}</strong></span>
                <span>{isQuote ? "Valid Until" : "Due Date"}: <strong className="text-navy">{doc.validUntil}</strong></span>
              </div>

              {/* Items */}
              <table className="mt-6 w-full text-sm">
                <thead>
                  <tr className="bg-navy text-left text-white">
                    <th className="rounded-l-lg px-4 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-widest">Service</th>
                    <th className="px-4 py-2.5 text-right font-mono text-[10px] font-semibold uppercase tracking-widest">Qty</th>
                    <th className="px-4 py-2.5 text-right font-mono text-[10px] font-semibold uppercase tracking-widest">Rate</th>
                    <th className="rounded-r-lg px-4 py-2.5 text-right font-mono text-[10px] font-semibold uppercase tracking-widest">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.items.map((it, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-4 py-3 text-slate-700">{it.desc || "—"}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{it.qty}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{money(it.rate)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-navy">{money((Number(it.qty) || 0) * (Number(it.rate) || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="mt-4 flex justify-end">
                <div className="w-64 space-y-1.5 text-sm">
                  <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{money(subtotal)}</span></div>
                  {doc.gst && <div className="flex justify-between text-slate-600"><span>GST (10%)</span><span>{money(gstAmt)}</span></div>}
                  <div className="flex justify-between rounded-lg bg-teal-600 px-3 py-2 font-display text-base font-extrabold text-white">
                    <span>Total</span><span>{money(total)}</span>
                  </div>
                </div>
              </div>

              {/* Notes + acceptance */}
              {doc.notes && (
                <div className="mt-8">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-teal-700">Notes</p>
                  <p className="mt-2 text-xs leading-relaxed text-slate-600">{doc.notes}</p>
                </div>
              )}
              {isQuote ? (
                <div className="mt-10 grid grid-cols-2 gap-10">
                  <div>
                    <div className="border-b border-slate-300 pb-6" />
                    <p className="mt-2 text-[11px] text-slate-500">Customer Signature — Acceptance of Quote</p>
                  </div>
                  <div>
                    <div className="border-b border-slate-300 pb-6" />
                    <p className="mt-2 text-[11px] text-slate-500">Date</p>
                  </div>
                </div>
              ) : (
                <div className="mt-10 rounded-lg bg-slate-50 p-4 text-xs leading-relaxed text-slate-600">
                  <strong className="text-navy">Payment:</strong> Please pay by the due date. Bank details: [BANK DETAILS]. Reference: {doc.docNo}.
                </div>
              )}
              <p className="mt-8 text-[10px] leading-relaxed text-slate-400">
                RD Cleaning Services{settings.abn ? ` · ABN ${settings.abn}` : ""} · {isQuote ? "This quote is valid until the date shown above and may be adjusted if the scope of work changes." : "Thank you for your business."}
              </p>
            </div>
          ) : (
            <div className="px-12 py-10">
              <p className="text-xs text-slate-400">{doc.date}</p>
              <div className="mt-10 space-y-4 text-sm leading-[2] text-slate-300">
                <p>Dear …</p>
                <p className="border-b border-dashed border-slate-200 pb-4"> </p>
                <p className="border-b border-dashed border-slate-200 pb-4"> </p>
                <p className="border-b border-dashed border-slate-200 pb-4"> </p>
                <p className="border-b border-dashed border-slate-200 pb-4"> </p>
                <p className="border-b border-dashed border-slate-200 pb-4"> </p>
                <p className="border-b border-dashed border-slate-200 pb-4"> </p>
                <p className="border-b border-dashed border-slate-200 pb-4"> </p>
                <p className="pt-6 text-slate-400">Kind regards,</p>
                <p className="font-display font-bold text-navy">RD Cleaning Services</p>
              </div>
            </div>
          )}

          {/* Branded footer strip */}
          <div className="mt-auto flex items-center justify-between bg-navy px-12 py-4">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-teal-400">Clean Today. Trusted Tomorrow.</p>
            <p className="font-mono text-[10px] uppercase tracking-wider text-slate-300">
              {settings.phone} · Perth WA{settings.abn ? ` · ABN ${settings.abn}` : ""}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
